import { useEffect, useState } from "react";

import { PAYMENT_METHOD, fetchCardInfo } from "../api/fetchCardInfo";
import type { IOrdersEdgesNode } from "../types/api/customer";
import type { IReturn } from "../types/api/return";
import type { Order } from "../types/generated/storefront.types";
import { extractId } from "../utils/common";

const STORE_NAME = import.meta.env.VITE_REACT_APP_SHOP_NAME as string;

// 결제 한 카드 정보 가져오기
export default function useOrderPayMethod({
  order,
  lang,
}: {
  order?: Order | IOrdersEdgesNode | IReturn["order"] | null;
  lang: "ko" | "en" | "jp";
}) {
  const [paymentInfo, setPaymentInfo] = useState<string>("-");

  const getCardInfo = async () => {
    const orderId = extractId(order?.id || "");
    if (!orderId) return;

    const response = await fetchCardInfo({ storeName: STORE_NAME, orderId, lang });

    if (response?.card) {
      setPaymentInfo(`${response.card.cardName}${response.card.cardType ? `(${response.card.cardType})` : ""}`);
      return;
    }

    if (response?.easyPayProvider) {
      setPaymentInfo(response.easyPayProvider);
      return;
    }

    setPaymentInfo("-");
  };

  const handlePaymentInfo = async () => {
    if (!order) return;

    if ((order as IOrdersEdgesNode).isOffline?.value === "true") return "성수 스토어 결제";

    const findTag = order.customAttributes?.find((item) => item.key === "paymentMethod");

    if (findTag?.value) {
      if (lang === "ko") {
        setPaymentInfo(findTag.value);
        return;
      }

      if (lang === "en") {
        const foundMethodValue = PAYMENT_METHOD[findTag.value as keyof typeof PAYMENT_METHOD];
        const paymentMethodValues = Object.values(PAYMENT_METHOD);

        // key로 매핑된 값이 있으면 사용, 없으면 value 목록에 존재하는지 확인
        if (foundMethodValue) {
          setPaymentInfo(foundMethodValue);
          return;
        }

        if (paymentMethodValues.includes(findTag.value)) {
          setPaymentInfo(findTag.value);
          return;
        }
      }
    }

    await getCardInfo();
  };

  useEffect(() => {
    void handlePaymentInfo();
  }, [order]);

  return paymentInfo;
}
